/* eslint-disable */
/**
 * ImportRoomsFromProposalModal — suggest and bulk-create hosp_rooms for a
 * property from its billed invoice or its linked proposal.
 *
 * Source priority: the latest issued invoice (what the customer is actually
 * paying for) by default, falling back to the most recent proposal that
 * carries a room breakdown. Beds are counted from bed lines only (toppers,
 * pillows, headboards etc are NOT beds). Rooms come from the invoice's own
 * sections when present, otherwise the proposal's room layout.
 *
 * Everything is a SUGGESTION the operator reviews and confirms — nothing is
 * auto-applied. "Replace existing rooms" clears the property's current rooms
 * first; otherwise the new rooms are added to whatever is already there.
 *
 * Rooms-not-configured is NOT a hard blocker on the POS pipeline (certificates
 * accept a null room_id, invoices don't touch rooms), but this tool exists to
 * unblock onboarding for large properties without typing each room by hand.
 */

import { useEffect, useMemo, useState } from 'react';
import { formatDate } from '../../../../utils/formatters';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import SearchSelect from '../../../../components/SearchSelect';

interface ScopeRoomItem { name?: string; item_name?: string; quantity?: number; }
interface ScopeRoom { name?: string; quantity?: number; items?: ScopeRoomItem[]; }

interface Proposal {
  id: string;
  reference_code: string;
  status: string | null;
  accepted_at: string | null;
  created_at: string;
  proposal_data: { scope?: { rooms?: ScopeRoom[] } } | null;
}

interface InvoiceItem {
  item_name: string | null;
  quantity: number | null;
  section_label: string | null;
  section_quantity: number | null;
  display_order: number | null;
}

interface Invoice {
  id: string;
  invoice_number: string | null;
  status: string | null;
  created_at: string;
  items: InvoiceItem[];
}

interface PreviewGroup {
  label: string;
  quantity: number;
  bedsPerRoom: number;
  toppersPerRoom: number;
  bedItemNames: string[];
}

interface Props {
  property: Record<string, any>;
  supabase: any;
  existingRoomCount: number;
  onClose: () => void;
  onSave: () => void | Promise<void>;
}

type Source = 'invoice' | 'proposal';

// Only items whose name starts with "bed" count toward the bed count. Toppers,
// pillows, headboards etc are ignored, so a 1-bed + 1-topper room is 1 bed.
function isBedItem(name: string | null | undefined): boolean {
  return (name || '').trim().toLowerCase().startsWith('bed');
}
function isTopperItem(name: string | null | undefined): boolean {
  return (name || '').trim().toLowerCase().startsWith('topper');
}

// ── Proposal scope → preview ────────────────────────────────────────────────
function sumScope(items: ScopeRoomItem[] | undefined, pred: (n: string) => boolean): number {
  if (!items) return 0;
  return items.filter(i => pred(i.name || i.item_name || '')).reduce((s, i) => s + (Number(i.quantity) || 0), 0);
}
function scopeBedLabels(items: ScopeRoomItem[] | undefined): string[] {
  if (!items) return [];
  return Array.from(new Set(
    items.filter(i => isBedItem(i.name || i.item_name)).map(i => (i.name || i.item_name || 'Bed').trim()),
  ));
}
function proposalPreview(rooms: ScopeRoom[] | undefined): PreviewGroup[] {
  if (!rooms) return [];
  return rooms
    .filter(r => (r.name || '').trim() && (Number(r.quantity) || 0) > 0)
    .map(r => ({
      label: (r.name || 'Room').trim(),
      quantity: Number(r.quantity) || 0,
      bedsPerRoom: sumScope(r.items, isBedItem),
      toppersPerRoom: sumScope(r.items, isTopperItem),
      bedItemNames: scopeBedLabels(r.items),
    }));
}

// ── Invoice items → preview ──────────────────────────────────────────────────
function invoiceHasSections(items: InvoiceItem[]): boolean {
  return items.some(it => (it.section_label || '').trim() !== '');
}
function invoiceBedTotal(items: InvoiceItem[]): number {
  return items.filter(it => isBedItem(it.item_name)).reduce((s, it) => s + (Number(it.quantity) || 0), 0);
}
// Each distinct section becomes a room type; section_quantity is how many such
// rooms; the section's bed lines give beds per room.
function invoiceSectionPreview(items: InvoiceItem[]): PreviewGroup[] {
  const groups = new Map<string, { items: InvoiceItem[]; qty: number }>();
  for (const it of items) {
    const label = (it.section_label || '').trim() || 'Room';
    if (!groups.has(label)) groups.set(label, { items: [], qty: Number(it.section_quantity) || 1 });
    groups.get(label)!.items.push(it);
  }
  return Array.from(groups.entries()).map(([label, g]) => ({
    label,
    quantity: g.qty,
    bedsPerRoom: g.items.filter(it => isBedItem(it.item_name)).reduce((s, it) => s + (Number(it.quantity) || 0), 0),
    toppersPerRoom: g.items.filter(it => isTopperItem(it.item_name)).reduce((s, it) => s + (Number(it.quantity) || 0), 0),
    bedItemNames: Array.from(new Set(
      g.items.filter(it => isBedItem(it.item_name)).map(it => (it.item_name || 'Bed').trim()),
    )),
  }));
}

export default function ImportRoomsFromProposalModal({
  property, supabase, existingRoomCount, onClose, onSave,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [source, setSource] = useState<Source>('proposal');
  const [selectedProposalId, setSelectedProposalId] = useState<string>('');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);

      const { data: propData, error: propErr } = await supabase
        .from('pos_proposals')
        .select('id, reference_code, status, accepted_at, created_at, proposal_data')
        .eq('property_id', property.id)
        .order('accepted_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (cancelled) return;
      if (propErr) { setError(propErr.message); setLoading(false); return; }

      const withRooms = (propData || []).filter((p: Proposal) =>
        Array.isArray(p?.proposal_data?.scope?.rooms) && (p.proposal_data!.scope!.rooms!.length > 0));

      // Latest issued (non-draft, non-void) invoice for the property's customer.
      let inv: Invoice | null = null;
      if (property.customer_id) {
        const { data: invRow } = await supabase
          .from('invoices')
          .select('id, invoice_number, status, created_at')
          .eq('customer_id', property.customer_id)
          .neq('status', 'draft')
          .neq('status', 'void')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (invRow) {
          const { data: items } = await supabase
            .from('invoice_items')
            .select('item_name, quantity, section_label, section_quantity, display_order')
            .eq('invoice_id', invRow.id)
            .order('display_order', { ascending: true });
          inv = { ...invRow, items: (items || []) } as Invoice;
        }
      }

      if (cancelled) return;
      setProposals(withRooms);
      if (withRooms[0]) setSelectedProposalId(withRooms[0].id);
      setInvoice(inv);
      // Default to the invoice when it actually carries beds, else the proposal.
      setSource(inv && invoiceBedTotal(inv.items) > 0 ? 'invoice' : 'proposal');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [property.id, property.customer_id, supabase]);

  const selectedProposal = proposals.find(p => p.id === selectedProposalId) || null;
  const propPreview = useMemo(() => proposalPreview(selectedProposal?.proposal_data?.scope?.rooms), [selectedProposal]);
  const propBedTotal = useMemo(() => propPreview.reduce((s, g) => s + g.quantity * g.bedsPerRoom, 0), [propPreview]);
  const invBedTotal = useMemo(() => (invoice ? invoiceBedTotal(invoice.items) : 0), [invoice]);
  const invSectioned = useMemo(() => !!invoice && invoiceHasSections(invoice.items), [invoice]);

  // Active suggestion:
  //  - invoice + sections → rooms from the invoice's own sections
  //  - invoice + flat list → borrow the proposal's room layout
  //  - proposal → proposal layout
  const preview = useMemo<PreviewGroup[]>(() => {
    if (source === 'invoice' && invoice && invSectioned) return invoiceSectionPreview(invoice.items);
    return propPreview;
  }, [source, invoice, invSectioned, propPreview]);

  const totalRooms = preview.reduce((s, g) => s + g.quantity, 0);
  const totalBeds = preview.reduce((s, g) => s + g.quantity * g.bedsPerRoom, 0);
  const totalToppers = preview.reduce((s, g) => s + g.quantity * g.toppersPerRoom, 0);

  const divergence = invoice && selectedProposal && invBedTotal !== propBedTotal
    ? { invoice: invBedTotal, proposal: propBedTotal } : null;

  // Invoice chosen, flat list, and no proposal layout to borrow — can't suggest rooms.
  const invoiceFlatNoLayout = source === 'invoice' && !!invoice && !invSectioned && propPreview.length === 0;

  const nothingToShow = !loading && proposals.length === 0 && (!invoice || invBedTotal === 0);

  async function handleImport() {
    if (preview.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const sourceRef = source === 'invoice' && invoice
        ? `invoice ${invoice.invoice_number || invoice.id}`
        : `proposal ${selectedProposal?.reference_code || selectedProposal?.id || ''}`.trim();

      const rows: Record<string, any>[] = [];
      for (const g of preview) {
        const toppersNote = g.toppersPerRoom > 0
          ? `${g.toppersPerRoom} topper${g.toppersPerRoom > 1 ? 's' : ''} per room (from ${sourceRef})`
          : null;
        for (let i = 1; i <= g.quantity; i++) {
          const label = `${g.label} ${i}`;
          rows.push({
            property_id: property.id,
            room_number: label,
            room_name: label,
            bed_count: g.bedsPerRoom || 1,
            room_type: 'standard',
            status: 'active',
            notes: toppersNote,
          });
        }
      }

      if (replaceExisting) {
        const { error: delErr } = await supabase.from('hosp_rooms').delete().eq('property_id', property.id);
        if (delErr) throw delErr;
      }

      const { error: insErr } = await supabase.from('hosp_rooms').insert(rows);
      if (insErr) throw insErr;

      // Belt + braces nudge: fire the AFTER trigger on hosp_rooms
      // for every active room so total_rooms / total_beds on
      // hosp_properties always lands. The trigger is the single
      // source of truth for those derived fields.
      await supabase
        .from('hosp_rooms')
        .update({ updated_at: new Date().toISOString() })
        .eq('property_id', property.id)
        .eq('status', 'active');

      await onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  const showProposalSelect = (source === 'proposal' || (source === 'invoice' && !invSectioned)) && proposals.length > 0;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1200 }}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Suggest rooms</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}><LoadingSpinner /></div>
          ) : nothingToShow ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-light)' }}>
              No invoice or proposal with a room breakdown was found for this property.
              Use <strong>Bulk Add</strong> to create rooms manually.
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Suggest from</label>
                <div className="flex gap-sm">
                  <button
                    type="button"
                    className={`btn ${source === 'invoice' ? 'btn-primary' : 'btn-secondary'} text-sm`}
                    onClick={() => setSource('invoice')}
                    disabled={!invoice || invBedTotal === 0}
                  >
                    Latest invoice{invoice?.invoice_number ? ` (${invoice.invoice_number})` : ''}
                  </button>
                  <button
                    type="button"
                    className={`btn ${source === 'proposal' ? 'btn-primary' : 'btn-secondary'} text-sm`}
                    onClick={() => setSource('proposal')}
                    disabled={proposals.length === 0}
                  >
                    Proposal
                  </button>
                </div>
              </div>

              {showProposalSelect && (
                <div className="form-group">
                  <label className="form-label">
                    {source === 'invoice' ? 'Room layout from proposal (invoice has no room breakdown)' : 'Proposal'}
                  </label>
                  <SearchSelect
                    disabled={proposals.length === 0}
                    options={proposals.map(p => {
                      const accepted = p.accepted_at ? ` · accepted ${formatDate(p.accepted_at)}` : '';
                      const status = p.status ? ` · ${p.status}` : '';
                      return { value: p.id, label: `${p.reference_code}${status}${accepted}` };
                    })}
                    value={selectedProposalId}
                    onChange={setSelectedProposalId}
                  />
                </div>
              )}

              {divergence && (
                <div className="alert alert-warning mb-md">
                  The invoice bills <strong>{divergence.invoice}</strong> bed{divergence.invoice !== 1 ? 's' : ''}, but the proposal layout totals <strong>{divergence.proposal}</strong>.
                  Check the rooms below match what was actually billed before importing.
                </div>
              )}

              {invoiceFlatNoLayout && (
                <div className="alert alert-warning mb-md">
                  The invoice bills {invBedTotal} bed{invBedTotal !== 1 ? 's' : ''} but has no room breakdown, and there is no proposal layout to borrow.
                  Switch to a proposal, or use <strong>Bulk Add</strong> to lay rooms out manually.
                </div>
              )}

              {existingRoomCount > 0 && (
                <div className="alert alert-warning mb-md">
                  This property already has <strong>{existingRoomCount}</strong> room{existingRoomCount !== 1 ? 's' : ''}.
                  <label className="flex items-center fw-500 mt-sm gap-sm">
                    <input type="checkbox" checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)} />
                    Replace existing rooms (delete the current {existingRoomCount} and rebuild)
                  </label>
                  {!replaceExisting && <div className="mt-xs">Otherwise the new rooms are added to the existing list.</div>}
                </div>
              )}

              {preview.length > 0 && (
                <>
                  <div className="text-sm text-muted mb-md">
                    Will {replaceExisting ? 'replace with' : 'create'} <strong style={{ color: 'var(--color-text)' }}>{totalRooms}</strong> room{totalRooms !== 1 ? 's' : ''}
                    {' '}with <strong style={{ color: 'var(--color-text)' }}>{totalBeds}</strong> bed{totalBeds !== 1 ? 's' : ''}
                    {totalToppers > 0 && (
                      <> and <strong style={{ color: 'var(--color-text)' }}>{totalToppers}</strong> topper{totalToppers !== 1 ? 's' : ''}</>
                    )}
                    {' '}in total. Toppers go in room notes, not the bed count. Review and adjust the rooms after importing.
                  </div>

                  <table className="data-table w-full">
                    <thead>
                      <tr>
                        <th className="text-left">Room type</th>
                        <th className="text-right">Rooms</th>
                        <th className="text-right">Beds each</th>
                        <th className="text-right">Toppers each</th>
                        <th className="text-left">Bed items</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((g, i) => (
                        <tr key={i}>
                          <td>
                            <div className="fw-500">{g.label}</div>
                            <div className="text-xs text-muted">
                              → {g.label} 1 … {g.label} {g.quantity}
                            </div>
                          </td>
                          <td className="text-right">{g.quantity}</td>
                          <td className="text-right">{g.bedsPerRoom || '—'}</td>
                          <td className="text-right">{g.toppersPerRoom || '—'}</td>
                          <td style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>
                            {g.bedItemNames.length ? g.bedItemNames.join(', ') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {error && (
                <div className="alert alert-warning mt-md">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={saving || loading || preview.length === 0}
          >
            {saving
              ? 'Saving…'
              : `${replaceExisting ? 'Replace with' : 'Import'} ${totalRooms || ''} room${totalRooms !== 1 ? 's' : ''}`.replace(/\s+/g, ' ').trim()}
          </button>
        </div>
      </div>
    </div>
  );
}
